import {
  Bell,
  Calendar,
  DollarSign,
  MapPin,
  Package,
  ShoppingBag,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "@/hooks/redux";
import api from "@/services/api";

interface BusinessMetrics {
  // Courier
  activeCouriers?: number;
  // Restaurant
  activeProducts?: number;
  // Fleet
  activeVehicles?: number;
  customerCount?: number;

  drivers?: number;
  // common
  monthlyRevenue: number[];

  recentOrders: {
    amount: number;
    customer: string;
    id: string;
    status: "delivered" | "pending" | "preparing" | "ready" | string;
    time: string;
  }[];
  serviceAreas?: number;

  totalOrders: number;
  totalRevenue: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [timeRange, setTimeRange] = useState<"month" | "week" | "year">(
    "month",
  );

  const [businessMetrics, setBusinessMetrics] =
    useState<BusinessMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [apiErrors, setApiErrors] = useState<null | string[]>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        setApiErrors(null);
        const res = await api.get(`/business/dashboard/comprehensive`);
        if (res?.data?.success) {
          setBusinessMetrics(res.data.data as BusinessMetrics);
        } else {
          setError("Failed to load dashboard data");
        }
      } catch (err: any) {
        console.error(err);
        // Friendly unauthorized message
        if (err?.response) {
          const status = err.response.status;
          if (status === 401 || status === 403) {
            setError(
              "You do not have access to this dashboard. If you need access, contact your account administrator or support.",
            );
            setBusinessMetrics(null);
            setLoading(false);
            return;
          }

          // Validation errors (400/422) — show friendly list
          if (status === 400 || status === 422) {
            const data = err.response.data || {};
            const messages: string[] = [];
            if (data.message) messages.push(String(data.message));
            if (data.errors) {
              if (Array.isArray(data.errors)) messages.push(...data.errors.map(String));
              else if (typeof data.errors === "object") {
                Object.values(data.errors).forEach((v: any) => {
                  if (Array.isArray(v)) messages.push(...v.map(String));
                  else messages.push(String(v));
                });
              }
            }
            if (messages.length === 0 && data.detail) messages.push(String(data.detail));
            setApiErrors(messages.length ? messages : ["There was a problem with your request."]);
            setError("Some inputs need attention. Please review the messages below.");
            setBusinessMetrics(null);
            setLoading(false);
            return;
          }
        }

        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [timeRange]);

  const formatCurrency = (value: number) => `R${value.toFixed(2)}`;

  const getBusinessTypeLabel = () => {
    switch (user?.role) {
      case "CourierOwner":
        return "Courier Service";
      case "FleetOwner":
        return "Fleet";
      case "RestaurantOwner":
        return "Restaurant";
      default:
        return "Business";
    }
  };

  const getMetricsCards = () => {
    const baseCards = [
      {
        change: "+12.5%",
        changeType: "positive" as const,
        color: "bg-green-500",
        icon: DollarSign,
        title: "Total Revenue",
        value: businessMetrics
          ? `R${businessMetrics.totalRevenue.toLocaleString()}`
          : "-",
      },
      {
        change: "+8.2%",
        changeType: "positive" as const,
        color: "bg-blue-500",
        icon: ShoppingBag,
        title: "Total Orders",
        value: businessMetrics ? businessMetrics.totalOrders.toLocaleString() : "-",
      },
    ];

    if (user?.role === "RestaurantOwner") {
      return [
        ...baseCards,
        {
          change: "+3",
          changeType: "positive" as const,
          color: "bg-orange-500",
          icon: Package,
          title: "Active Menu Items",
          value: businessMetrics?.activeProducts
            ? businessMetrics.activeProducts.toString()
            : "-",
        },
        {
          change: "+15.3%",
          changeType: "positive" as const,
          color: "bg-purple-500",
          icon: Users,
          title: "Customers",
          value: businessMetrics?.customerCount
            ? businessMetrics.customerCount.toString()
            : "-",
        },
      ];
    }

    if (user?.role === "FleetOwner") {
      return [
        ...baseCards,
        {
          change: "+2",
          changeType: "positive" as const,
          color: "bg-orange-500",
          icon: Package,
          title: "Active Vehicles",
          value: businessMetrics?.activeVehicles
            ? businessMetrics.activeVehicles.toString()
            : "-",
        },
        {
          change: "+1",
          changeType: "positive" as const,
          color: "bg-purple-500",
          icon: Users,
          title: "Drivers",
          value: businessMetrics?.drivers ? businessMetrics.drivers.toString() : "-",
        },
      ];
    }

    // CourierOwner
    return [
      ...baseCards,
      {
        change: "+2",
        changeType: "positive" as const,
        color: "bg-orange-500",
        icon: Users,
        title: "Active Couriers",
        value: businessMetrics?.activeCouriers
          ? businessMetrics.activeCouriers.toString()
          : "-",
      },
      {
        change: "0",
        changeType: "neutral" as const,
        color: "bg-purple-500",
        icon: MapPin,
        title: "Service Areas",
        value: businessMetrics?.serviceAreas ? businessMetrics.serviceAreas.toString() : "-",
      },
    ];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "preparing":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "ready":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  return (
    <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="mb-1 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:mb-2 sm:text-3xl">
              {getBusinessTypeLabel()} Dashboard
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              Welcome back! Here's what's happening with your business today.
            </p>
          </div>

          {/* Time Range Selector */}
          <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
            {(["week", "month", "year"] as const).map((range) => (
              <button
                className={`rounded-md px-3 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-sm ${
                  timeRange === range
                    ? "bg-white text-blue-600 shadow-sm dark:bg-gray-800 dark:text-blue-400"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                }`}
                key={range}
                onClick={() => setTimeRange(range)}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      {error && error.includes("do not have access") ? (
        <div className="mb-6 rounded-lg bg-white p-6 text-center shadow-md dark:bg-gray-800">
          <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Access Restricted</h2>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <div className="flex justify-center gap-3">
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
              onClick={() => navigate("/contact-us")}
            >
              Contact Support
            </button>
            <button
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={() => navigate("/auth/login")}
            >
              Switch Account
            </button>
          </div>
        </div>
      ) : (
        error && (
        <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-400">
          <p className="font-medium">{error}</p>
          {apiErrors && apiErrors.length > 0 && (
            <ul className="mt-2 list-inside list-disc text-sm">
              {apiErrors.map((m, i) => (
                <li className="truncate" key={i}>
                  {m}
                </li>
              ))}
            </ul>
          )}
        </div>
        )
      )}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
        {getMetricsCards().map((metric, index) => (
          <div
            className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6"
            key={index}
          >
            <div className="mb-3 flex items-center justify-between sm:mb-4">
              <div
                className={`rounded-full p-2 text-white sm:p-3 ${metric.color}`}
              >
                <metric.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <span
                className={`text-xs font-medium sm:text-sm ${
                  metric.changeType === "positive"
                    ? "text-green-600 dark:text-green-400"
                    : metric.changeType === "neutral"
                      ? "text-gray-600 dark:text-gray-400"
                      : "text-red-600 dark:text-red-400"
                }`}
              >
                {metric.change}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                {metric.title}
              </p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                {metric.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:gap-6 lg:gap-8 xl:grid-cols-3">
        {/* Revenue Chart */}
        <div className="xl:col-span-2">
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
            <div className="mb-3 flex items-center justify-between sm:mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                Revenue Overview
              </h3>
              <TrendingUp className="h-4 w-4 text-green-500 sm:h-5 sm:w-5" />
            </div>
            <div className="flex h-48 items-end justify-between gap-1 sm:h-64 sm:gap-2">
              {(businessMetrics?.monthlyRevenue || []).map((revenue, index) => (
                <div className="flex flex-1 flex-col items-center" key={index}>
                  <div
                    className="w-full max-w-[32px] rounded-t-sm bg-gradient-to-t from-blue-500 to-blue-400"
                    style={{
                      height: `${(revenue / Math.max(...(businessMetrics?.monthlyRevenue || [1]))) * 160}px`,
                    }}
                  ></div>
                  <span className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-xs">
                    {
                      [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                      ][index]
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Orders/Activity */}
          <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
          <div className="mb-3 flex items-center justify-between sm:mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Recent {user?.role === "RestaurantOwner" ? "Orders" : "Activity"}
            </h3>
            <Bell className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5" />
          </div>
          <div className="space-y-3 sm:space-y-4">
            {(businessMetrics?.recentOrders || []).map((order) => (
              <div
                className="flex items-center justify-between gap-2"
                key={order.id}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100 sm:text-base">
                    {order.id}
                  </p>
                  <p className="truncate text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    {order.customer} • {order.time}
                  </p>
                </div>
                  <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 sm:text-base">
                    {formatCurrency(order.amount)}
                  </p>
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium sm:text-xs ${getStatusColor(order.status)}`}
                  >
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {user?.role === "RestaurantOwner" && (
          <>
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Menu Management
              </h3>
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                Add new items, update prices, and manage your menu.
              </p>
              <button
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                onClick={() => navigate("/business/management?tab=items")}
              >
                Manage Menu
              </button>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Order Management
              </h3>
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                Track and manage incoming orders from customers.
              </p>
              <button
                className="w-full rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
                onClick={() => navigate("/business/management?tab=orders")}
              >
                View Orders
              </button>
            </div>
          </>
        )}

        {user?.role === "FleetOwner" && (
          <>
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Fleet Management
              </h3>
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                Manage your vehicles, maintenance, and assignments.
              </p>
              <button
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                onClick={() => navigate("/business/management?tab=vehicles")}
              >
                Manage Fleet
              </button>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Driver Management
              </h3>
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                Hire, manage, and track your driver performance.
              </p>
              <button
                className="w-full rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
                onClick={() => navigate("/business/management?tab=drivers")}
              >
                Manage Drivers
              </button>
            </div>
          </>
        )}

        {user?.role === "CourierOwner" && (
          <>
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Courier Management
              </h3>
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                Manage your courier staff and delivery zones.
              </p>
              <button
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                onClick={() => navigate("/business/management?tab=couriers")}
              >
                Manage Couriers
              </button>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Service Areas
              </h3>
              <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                Define and manage your service coverage areas.
              </p>
              <button
                className="w-full rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
                onClick={() => navigate("/business/management?tab=zones")}
              >
                Manage Areas
              </button>
            </div>
          </>
        )}

        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
          <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
            Analytics & Reports
          </h3>
          <p className="mb-3 text-xs text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
            View detailed analytics and generate business reports.
          </p>
          <button
            className="w-full rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700"
            onClick={() => navigate("/business/analytics")}
          >
            View Analytics
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
